// Copiar el cuadro actual de un video al portapapeles —
// ADR-2026-09-26-copiar-cuadro-del-estudio-al-portapapeles.
//
// El Estudio ya dibuja el `<video>` de cada clip con `crossOrigin="anonymous"` (lo
// necesita el pincel de borrado para pasar el cuadro a canvas), así que el cuadro que se
// está viendo se puede exportar tal cual: canvas → PNG → portapapeles. Sin red, sin
// backend, sin créditos. Si el navegador no deja escribir imágenes en el portapapeles
// (Firefox, o el permiso denegado), el mismo PNG se descarga: nunca "no pasó nada".
//
// Lo que no pide DOM se testea con dobles: el nombre, el soporte, la espera del cuadro
// exacto y la escritura al portapapeles. El canvas pide un `<video>` real.

/** Lo que hace falta para escribir una imagen al portapapeles. */
export interface EntornoDePortapapeles {
  ClipboardItem?: unknown;
  clipboardWrite?: unknown;
}

const entornoReal = (): EntornoDePortapapeles => ({
  ClipboardItem: typeof ClipboardItem === 'undefined' ? undefined : ClipboardItem,
  // `write?.`: hay navegadores con `clipboard` pero sin `write` (Firefox viejo); el bind
  // a secas tiraba y el respaldo de descarga nunca llegaba (revisión de Greptile al PR #149).
  clipboardWrite:
    typeof navigator === 'undefined' || !navigator.clipboard
      ? undefined
      : navigator.clipboard.write?.bind(navigator.clipboard),
});

export function puedeCopiarImagenes(entorno: EntornoDePortapapeles = entornoReal()): boolean {
  return typeof entorno.ClipboardItem === 'function' && typeof entorno.clipboardWrite === 'function';
}

/** `captura-<clip>-<t>s.png`: el nombre del PNG cuando se descarga en vez de copiarse. */
export function nombreDelCuadro(nombreDelClip: string | undefined, tSeg: number): string {
  const base = (nombreDelClip || 'clip')
    .trim()
    .replace(/\.[a-z0-9]{2,5}$/i, '')
    .replace(/[^\w\-. ]+/g, '_')
    .replace(/\s+/g, '-')
    .replace(/[.\-_]+$/, '')
    .slice(0, 60) || 'clip';
  const t = Math.max(0, tSeg);
  return `captura-${base}-${t.toFixed(1)}s.png`;
}

/** Lo mínimo de un `<video>` que la espera necesita (para poder probarla sin DOM). */
export interface VideoQueBusca {
  currentTime: number;
  seeking?: boolean;
  addEventListener(tipo: 'seeked', f: () => void): void;
  removeEventListener(tipo: 'seeked', f: () => void): void;
}

/** Más lejos que esto del segundo pedido, se hace un seek antes de capturar. El preview
 *  tolera hasta 0,15 s para no buscar a cada rato mientras reproduce; para un PNG eso es
 *  hasta cuatro cuadros de diferencia (revisión de Greptile al PR #149). */
export const TOLERANCIA_DE_CUADRO_S = 0.02;
const ESPERA_DE_SEEK_MS = 2000;
/** Cuántas veces se vuelve a buscar si el `seeked` que llegó dejó el video en otro lado
 *  (otro pedido movió el cursor en el medio). */
const REINTENTOS_DE_SEEK = 3;

/**
 * Deja el video EXACTAMENTE en `tSeg` (segundos del archivo) antes de capturar: si el
 * cursor se acaba de mover, el `<video>` puede estar todavía buscando y el canvas
 * saldría con el cuadro anterior. Resuelve al `seeked` (o enseguida si ya está ahí);
 * con un tope por si el evento nunca llega. Un `seeked` que deja el video en OTRO
 * segundo (alguien más lo movió) no vale: se vuelve a buscar.
 */
export async function esperarCuadro(
  video: VideoQueBusca,
  tSeg: number,
  esperaMs = ESPERA_DE_SEEK_MS,
): Promise<void> {
  const destino = Math.max(0, tSeg);
  const estaEnDestino = () => !video.seeking && Math.abs(video.currentTime - destino) <= TOLERANCIA_DE_CUADRO_S;
  for (let intento = 0; intento <= REINTENTOS_DE_SEEK; intento++) {
    if (estaEnDestino()) return;
    const llego = await new Promise<boolean>((resolve) => {
      let listo = false;
      const terminar = (porEvento: boolean) => {
        if (listo) return;
        listo = true;
        clearTimeout(reloj);
        video.removeEventListener('seeked', alSeeked);
        resolve(porEvento);
      };
      const alSeeked = () => terminar(true);
      const reloj = setTimeout(() => terminar(false), esperaMs);
      video.addEventListener('seeked', alSeeked);
      if (Math.abs(video.currentTime - destino) > TOLERANCIA_DE_CUADRO_S) {
        try {
          video.currentTime = destino;
        } catch {
          terminar(false);
        }
      }
    });
    // Sin evento (tope o seek imposible) no hay más que esperar.
    if (!llego) return;
  }
}

/** Una captura por vez sobre el mismo `<video>`: dos pedidos seguidos en distintos
 *  segundos se turnan en vez de pelearse el seek (revisión de Greptile al PR #149). */
const colaPorVideo = new WeakMap<object, Promise<unknown>>();
export function enColaDelVideo<T>(video: object, tarea: () => Promise<T>): Promise<T> {
  const anterior = colaPorVideo.get(video) ?? Promise.resolve();
  const propia = anterior.then(tarea, tarea);
  // La cola nunca se rompe por un fallo: el siguiente arranca igual.
  colaPorVideo.set(video, propia.catch(() => undefined));
  return propia;
}

/** El cuadro que el `<video>` está mostrando, como PNG a resolución nativa. Con `tSeg`,
 *  primero lo lleva a ese segundo. Tira si el video no cargó o el canvas quedó "tainted"
 *  (video servido sin CORS). */
export function cuadroDelVideo(video: HTMLVideoElement, tSeg?: number): Promise<Blob> {
  return enColaDelVideo(video, () => capturar(video, tSeg));
}

async function capturar(video: HTMLVideoElement, tSeg?: number): Promise<Blob> {
  if (!video.videoWidth || !video.videoHeight) throw new Error('El video todavía no cargó.');
  if (typeof tSeg === 'number') await esperarCuadro(video, tSeg);
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('No se pudo crear el canvas.'));
    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No se pudo exportar el cuadro.'))), 'image/png');
    } catch (e) {
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/**
 * Escribe la imagen al portapapeles. `true` si quedó copiada; `false` si el navegador no
 * lo soporta o no lo permitió (el que llama decide el respaldo, hoy: descargar).
 *
 * Safari sólo acepta la escritura mientras dura el gesto del usuario, y el PNG llega
 * asíncrono (seek + `toBlob`): por eso se le pasa la PROMESA a `ClipboardItem` (Safari y
 * Chrome recientes la aceptan). Si el navegador no toma promesas, se espera el blob y se
 * vuelve a intentar.
 */
export async function copiarImagenAlPortapapeles(
  imagen: Promise<Blob>,
  entorno: EntornoDePortapapeles = entornoReal(),
): Promise<boolean> {
  if (!puedeCopiarImagenes(entorno)) return false;
  const Item = entorno.ClipboardItem as new (partes: Record<string, Blob | Promise<Blob>>) => unknown;
  const escribir = entorno.clipboardWrite as (items: unknown[]) => Promise<void>;
  try {
    await escribir([new Item({ 'image/png': imagen })]);
    return true;
  } catch {
    try {
      const blob = await imagen;
      await escribir([new Item({ 'image/png': blob })]);
      return true;
    } catch {
      return false;
    }
  }
}

/** Respaldo: el mismo PNG, descargado. */
export function descargarImagen(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
